"use strict";
/**
 * Supported currencies catalogue (EXFX-5)
 *
 * Single source of truth for currency lists used across the platform:
 *   - frontends (Core Frontend, Merchant Admin, Merchant Storefront)
 *   - backend Zod schemas (Core Backend, Merchant Backend)
 *   - PGW exchange-rate router (CurrencyClassifier)
 *   - bootstrap defaulting (Merchant Backend, Core Backend onboarding)
 *
 * Adding a new currency:
 *   1. Update SUPPORTED_FIAT or SUPPORTED_CRYPTO here
 *   2. Verify provider coverage:
 *      - fiat → ExchangeRate-API.io supports all ISO 4217
 *      - crypto → Bybit must list `${CODE}USDT` spot ticker
 *   3. Add a sanity range to PGW's RateValidator
 *   4. (For new fiat with merchant onboarding) add COUNTRY_CURRENCY_MAP entry
 *   5. Bump shared-utils-lib minor version, publish, update consumers
 *
 * Removing a currency is a breaking change — coordinate via deprecation.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.COUNTRY_CURRENCY_MAP = exports.SUPPORTED_CURRENCIES = exports.STABLECOINS = exports.SUPPORTED_CRYPTO = exports.SUPPORTED_FIAT = void 0;
exports.isFiat = isFiat;
exports.isCrypto = isCrypto;
exports.isStablecoin = isStablecoin;
exports.isSupportedCurrency = isSupportedCurrency;
exports.classifyCurrency = classifyCurrency;
exports.normalizeForConversion = normalizeForConversion;
// ---------------------------------------------------------------------------
// Tuples + types
// ---------------------------------------------------------------------------
/** Fiat currencies the platform actively supports (display + conversion). */
exports.SUPPORTED_FIAT = ['ARS', 'USD', 'EUR', 'BRL'];
/** Native crypto assets the platform routes through Bybit. */
exports.SUPPORTED_CRYPTO = ['BTC', 'ETH', 'USDT', 'TRX', 'SOL', 'BNB'];
/** USD-pegged stablecoins — collapsed to USD by `normalizeForConversion`. */
exports.STABLECOINS = ['USDT', 'USDC', 'DAI', 'BUSD'];
/** Union of all supported currency codes. */
exports.SUPPORTED_CURRENCIES = [...exports.SUPPORTED_FIAT, ...exports.SUPPORTED_CRYPTO];
// ---------------------------------------------------------------------------
// Country mapping (onboarding default only — never runtime fallback)
// ---------------------------------------------------------------------------
/**
 * ISO-3166 alpha-2 country code → default fiat currency for new merchants.
 *
 * **Use ONLY at onboarding** (when creating a `merchants` row). At runtime
 * the source of truth is `merchants.currency` (see EXFX-3); this map exists
 * to seed that column with a sensible default.
 *
 * The map intentionally only contains countries whose currency is in
 * `SUPPORTED_FIAT`. LATAM countries with non-supported currencies (CL/MX/CO/
 * PE/UY) are commented out — uncomment after their fiat is added to
 * `SUPPORTED_FIAT` and provider coverage is verified.
 */
exports.COUNTRY_CURRENCY_MAP = {
    AR: 'ARS',
    US: 'USD',
    BR: 'BRL',
    // Eurozone countries (subset — ES/IT/FR/DE/PT/etc. all map to EUR)
    ES: 'EUR',
    // Awaiting SUPPORTED_FIAT extension:
    // CL: 'CLP', MX: 'MXN', CO: 'COP', PE: 'PEN', UY: 'UYU',
};
// ---------------------------------------------------------------------------
// Type guards + normalization
// ---------------------------------------------------------------------------
function isFiat(currency) {
    return exports.SUPPORTED_FIAT.includes(currency.toUpperCase());
}
function isCrypto(currency) {
    return exports.SUPPORTED_CRYPTO.includes(currency.toUpperCase());
}
function isStablecoin(currency) {
    return exports.STABLECOINS.includes(currency.toUpperCase());
}
function isSupportedCurrency(currency) {
    return exports.SUPPORTED_CURRENCIES.includes(currency.toUpperCase());
}
function classifyCurrency(currency) {
    const c = currency.toUpperCase();
    // Stablecoins are technically also crypto — return the more specific kind first
    if (isStablecoin(c))
        return 'stablecoin';
    if (isFiat(c))
        return 'fiat';
    if (isCrypto(c))
        return 'crypto';
    return 'unknown';
}
/**
 * Normalize a currency code for routing/conversion purposes.
 * Stablecoins collapse to 'USD' (pegged 1:1).
 */
function normalizeForConversion(currency) {
    const c = currency.toUpperCase();
    return isStablecoin(c) ? 'USD' : c;
}
